/**
 * Compliance v4 — contrato HTTP compartilhado com o backend (2026-09-16).
 *
 * Fonte: o contrato acordado entre as duas pontas (espelhado no deliverable do
 * backend) + §3 do handoff v4. **Nada aqui é inventado**: se um campo não está
 * lá, não está aqui. Os pontos em aberto levam `// ⚠️ assumido — §9`.
 *
 * Dois blocos:
 *  - **catálogo global** (padrões, grupos, perguntas, opções) — sem `idCliente`,
 *    editado só pela IOX em `3a` (BR-23, reescrito em 16/09: Administrador +
 *    Arquiteto);
 *  - **dados por cliente** (questionário, evidências, sugestões) — sempre com
 *    `?idCliente=` e sempre sob `chaveDeQuery` (BR-19).
 *
 * 🔒 BR-22 — a IA é a única avaliadora. `veredito` é o único parecer; não existe
 * fila de validação do arquiteto. A "nota para IA" é o retorno do arquiteto SOBRE
 * o parecer, não um parecer paralelo.
 */
import { z } from 'zod'

import { id } from './comum'

/** ISO 8601 com hora — o contrato não fixa precisão, então não validamos formato. */
const instante = z.string().min(1)

// ── Catálogo global ─────────────────────────────────────────────────────────

export const statusPadraoSchema = z.enum(['rascunho', 'ativo', 'encerrado'])

export const padraoSchema = z.object({
  id,
  nome: z.string(),
  ano: z.number().int(),
  norma: z.string(),
  status: statusPadraoSchema,
  origem: z.discriminatedUnion('tipo', [
    z.object({ tipo: z.literal('zero') }),
    z.object({ tipo: z.literal('importado'), padraoOrigemId: id }),
  ]),
  criadoEm: instante,
})
export type Padrao = z.infer<typeof padraoSchema>

export const grupoSchema = z.object({
  id,
  padraoId: id,
  paiId: id.nullable(),
  codigo: z.string(),
  titulo: z.string(),
  /** Só no 1º nível. Subgrupo não tem peso — soma dentro do pai. */
  peso: z.number().nullable(),
  ordem: z.number().int(),
  qtdItens: z.number().int(),
  /** Calculada pelo servidor, nunca digitada. */
  notaMaxima: z.number(),
})
export type Grupo = z.infer<typeof grupoSchema>

export const opcaoRespostaSchema = z.object({
  id,
  rotulo: z.string(),
  pontuacao: z.number(),
  ordem: z.number().int(),
})
export type OpcaoResposta = z.infer<typeof opcaoRespostaSchema>

export const perguntaSchema = z.object({
  id,
  padraoId: id,
  grupoId: id,
  codigo: z.string(),
  enunciado: z.string(),
  orientacaoIA: z.string().nullable(),
  ordem: z.number().int(),
  opcoes: z.array(opcaoRespostaSchema),
})
export type Pergunta = z.infer<typeof perguntaSchema>

export const estruturaSchema = z.object({
  padrao: padraoSchema,
  grupos: z.array(grupoSchema),
  perguntas: z.array(perguntaSchema),
})
export type Estrutura = z.infer<typeof estruturaSchema>

export const resultadoDeBuscaSchema = z.object({
  padraoId: id,
  padraoNome: z.string(),
  ano: z.number().int(),
  perguntaId: id,
  codigo: z.string(),
  enunciado: z.string(),
})
export type ResultadoDeBusca = z.infer<typeof resultadoDeBuscaSchema>

// ── Questionário (por cliente) ──────────────────────────────────────────────

export const tipoEvidenciaSchema = z.enum(['pdf', 'docx', 'link'])
export type TipoEvidencia = z.infer<typeof tipoEvidenciaSchema>

export const situacaoVereditoSchema = z.enum(['aderente', 'concorda', 'discorda', 'sem-evidencia'])

export const itemQuestionarioSchema = z.object({
  perguntaId: id,
  resposta: z
    .object({
      opcaoId: id.nullable(),
      observacoes: z.string().nullable(),
      atualizadaEm: instante,
    })
    .nullable(),
  evidencias: z.array(z.object({ id, nome: z.string(), tipo: tipoEvidenciaSchema })),
  notaParaIA: z
    .object({ nota: z.number().int().min(1).max(10), avaliadoPor: z.string(), avaliadoEm: instante })
    .nullable(),
  veredito: z
    .object({
      situacao: situacaoVereditoSchema,
      recomendacao: z.string().nullable(),
      geradoEm: instante,
    })
    .nullable(),
})
export type ItemQuestionario = z.infer<typeof itemQuestionarioSchema>

export const questionarioSchema = z.object({
  totais: z.object({
    nota: z.number(),
    notaMaxima: z.number(),
    percentual: z.number(),
    respondidas: z.number().int(),
    total: z.number().int(),
    comEvidencia: z.number().int(),
    divergencias: z.number().int(),
  }),
  itens: z.array(itemQuestionarioSchema),
})
export type Questionario = z.infer<typeof questionarioSchema>

export const sugestaoSchema = z.object({
  id,
  evidencia: z.object({ id, nome: z.string(), tipo: tipoEvidenciaSchema }),
  perguntaDestino: z.object({ id, codigo: z.string(), enunciado: z.string() }),
  vinculoAnterior: z.object({ codigo: z.string(), ano: z.number().int() }).nullable(),
  confianca: z.enum(['alta', 'media', 'baixa']),
  estado: z.enum(['pendente', 'aceita', 'descartada']),
})
export type Sugestao = z.infer<typeof sugestaoSchema>

// ── Documentos (Lambda Gestão de Documentos) ────────────────────────────────

export const vinculoSchema = z.object({
  padraoId: id,
  ano: z.number().int(),
  perguntaId: id,
  codigo: z.string(),
  origem: z.enum(['manual', 'sugestao-ia']),
})

export const evidenciaSchema = z.object({
  id,
  nome: z.string(),
  tipo: tipoEvidenciaSchema,
  urlExterna: z.string().nullable(),
  tamanhoBytes: z.number().int().nullable(),
  enviadaEm: instante,
  observacoes: z.string().nullable(),
  vinculos: z.array(vinculoSchema),
})
export type Evidencia = z.infer<typeof evidenciaSchema>

/** A resposta de `upload-url` — um PUT presignado direto no S3. */
export const urlDeUploadSchema = z.object({
  uploadUrl: z.string().min(1),
  chave: z.string().min(1),
  expiraEm: instante,
  cabecalhos: z.record(z.string(), z.string()),
})
export type UrlDeUpload = z.infer<typeof urlDeUploadSchema>

export const urlDeDownloadSchema = z.object({ url: z.string().min(1), expiraEm: instante })

export const manualSchema = z.object({
  id,
  padraoId: id,
  nome: z.string(),
  tipo: z.enum(['pdf', 'docx']),
  tamanhoBytes: z.number().int(),
  enviadoEm: instante,
})
export type Manual = z.infer<typeof manualSchema>
